// Logging utility for the SmartCHG application
// 
// This logger displays logs in the browser console AND stores them in files on the user's machine.
// It automatically creates a 'logs' directory in the user-selected location and stores daily log files.
// 
// Features:
// - Shows logs in browser console for immediate debugging
// - Stores logs in text files on the local machine (similar to backend)
// - Automatic cleanup of logs older than 7 days
// - No browser storage (localStorage) - only file storage
// - Simple text format like backend logs
//
// Usage in browser console:
// - requestLogDirectory() - Select a directory for log storage
// - getStoredLogs(7) - Get logs from last 7 days
// - exportStoredLogs() - Export logs as text
// - getLogStats() - Get log statistics
// - clearStoredLogs() - Clear all stored logs
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
  error?: Error;
}

class Logger {
  private logLevel: LogLevel;
  private retentionDays = 7; // Keep logs for 7 days

  constructor() {
    // Set log level based on environment
    this.logLevel = process.env.NODE_ENV === 'production' ? LogLevel.ERROR : LogLevel.INFO;
    
    // Initialize file logging and cleanup old logs
    this.initializeFileLogging();
    this.cleanupOldLogs();
  }

  private initializeFileLogging(): void {
    try {
      // Try to use the File System Access API for modern browsers
      if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
        this.setupFileSystemLogging();
      } else {
        // No fallback - logs will only show in console
        console.warn('File System Access API not available - logs will only appear in console');
      }
    } catch (error) {
      // Silently fail if logging setup fails
    }
  }

  private setupFileSystemLogging(): void {
    // Use File System Access API to create log files
    // This will prompt user to select a directory for logs
    this.requestLogDirectory();
  }

  private async requestLogDirectory(): Promise<void> {
    try {
      // Request permission to access a directory for logs
      const dirHandle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });
      
      // Store the directory handle for later use
      (window as any).logDirectoryHandle = dirHandle;
      
      // Create logs directory if it doesn't exist
      await this.ensureLogsDirectory(dirHandle);
    } catch (error) {
      // User cancelled or permission denied - logs will only show in console
      console.warn('Log directory not set - logs will only appear in console');
    }
  }

  private async ensureLogsDirectory(dirHandle: any): Promise<void> {
    try {
      // Check if 'logs' directory exists, create if not
      try {
        await dirHandle.getDirectoryHandle('logs');
      } catch {
        await dirHandle.getDirectoryHandle('logs', { create: true });
      }
    } catch (error) {
      // Silently fail if directory creation fails
    }
  }


  private async cleanupOldLogs(): Promise<void> {
    if (typeof window === 'undefined') return;
    
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - this.retentionDays);
      
      // Clean up file system logs if available
      if ((window as any).logDirectoryHandle) {
        await this.cleanupFileSystemLogs(cutoffDate);
      }
    } catch (error) {
      // Silently fail if cleanup fails
    }
  }

  private async cleanupFileSystemLogs(cutoffDate: Date): Promise<void> {
    try {
      const dirHandle = (window as any).logDirectoryHandle;
      const logsDir = await dirHandle.getDirectoryHandle('logs');
      
      // Get all files in the logs directory
      const files = [];
      for await (const [name, handle] of logsDir.entries()) {
        if (handle.kind === 'file' && name.startsWith('frontend-logs-') && name.endsWith('.log')) {
          const dateStr = name.replace('frontend-logs-', '').replace('.log', '');
          const logDate = new Date(dateStr);
          
          if (logDate < cutoffDate) {
            files.push(name);
          }
        }
      }
      
      // Remove old log files
      for (const fileName of files) {
        try {
          await logsDir.removeEntry(fileName);
        } catch (error) {
          // Silently fail if file removal fails
        }
      }
    } catch (error) {
      // Silently fail if file system cleanup fails
    }
  }


  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }


  private addLog(entry: LogEntry): void {
    // Show log in console
    this.logToConsole(entry);
    
    // Store in file
    this.storeLogToFile(entry);
  }

  private logToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toLocaleString();
    const levelName = LogLevel[entry.level];
    const logMessage = `[${timestamp}] [${levelName}] [${entry.category}] ${entry.message}`;
    
    // Add data and error if present
    const logData = entry.data ? { data: entry.data } : {};
    const logError = entry.error ? { error: entry.error } : {};
    
    // Format for terminal output
    const terminalMessage = `${logMessage}${entry.data ? ' ' + JSON.stringify(entry.data) : ''}${entry.error ? ' ' + entry.error.message : ''}`;
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(logMessage, logData, logError);
        break;
      case LogLevel.INFO:
        console.info(logMessage, logData, logError);
        break;
      case LogLevel.WARN:
        console.warn(logMessage, logData, logError);
        break;
      case LogLevel.ERROR:
        console.error(logMessage, logData, logError);
        break;
      default:
        console.log(logMessage, logData, logError);
    }
    
    // Send to backend for terminal display
    this.sendLogToBackend(entry, terminalMessage);
  }

  private sendLogToBackend(entry: LogEntry, terminalMessage: string): void {
    // Backend logging disabled - logs are now stored locally only
    // This method is kept for future use if needed
  }

  private async storeLogToFile(entry: LogEntry): Promise<void> {
    if (typeof window === 'undefined') return;
    
    try {
      // Only store in file system if directory is set
      if ((window as any).logDirectoryHandle) {
        await this.writeToFileSystem(entry);
      }
    } catch (error) {
      // Silently fail if file storage fails
    }
  }

  private async writeToFileSystem(entry: LogEntry): Promise<void> {
    try {
      const dirHandle = (window as any).logDirectoryHandle;
      const logsDir = await dirHandle.getDirectoryHandle('logs');
      
      const today = new Date().toISOString().split('T')[0];
      const fileName = `frontend-logs-${today}.log`;
      
      // Format log entry like backend logs
      const timestamp = new Date(entry.timestamp).toISOString().replace('T', ' ').substring(0, 19);
      const levelName = LogLevel[entry.level].padEnd(5);
      const logLine = `${timestamp} [${levelName}] [${entry.category}] ${entry.message}`;
      
      // Get existing log content
      let existingContent = '';
      try {
        const fileHandle = await logsDir.getFileHandle(fileName);
        const file = await fileHandle.getFile();
        existingContent = await file.text();
      } catch {
        // File doesn't exist yet, start with empty content
        existingContent = '';
      }
      
      // Add new log line
      const newContent = existingContent + logLine + '\n';
      
      // Write back to file
      const fileHandle = await logsDir.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(newContent);
      await writable.close();
    } catch (error) {
      // Silently fail if file system fails
    }
  }


  debug(category: string, message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.DEBUG,
      category,
      message,
      data
    };
    
    this.addLog(entry);
  }

  info(category: string, message: string, data?: any): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.INFO,
      category,
      message,
      data
    };
    
    this.addLog(entry);
  }

  warn(category: string, message: string, data?: any, error?: Error): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.WARN,
      category,
      message,
      data,
      error
    };
    
    this.addLog(entry);
  }

  error(category: string, message: string, error?: Error, data?: any): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel.ERROR,
      category,
      message,
      data,
      error
    };
    
    this.addLog(entry);
  }

  // API-specific logging methods
  apiRequest(endpoint: string, method: string, data?: any): void {
    this.info('API', `${method} ${endpoint}`, data);
  }

  apiResponse(endpoint: string, method: string, status: number, data?: any): void {
    if (status >= 400) {
      this.error('API', `${method} ${endpoint} failed with status ${status}`, undefined, data);
    } else {
      this.info('API', `${method} ${endpoint} succeeded with status ${status}`, data);
    }
  }

  apiError(endpoint: string, method: string, error: Error, data?: any): void {
    this.error('API', `${method} ${endpoint} failed`, error, data);
  }

  // Component-specific logging methods
  componentMount(componentName: string, props?: any): void {
    this.debug('Component', `${componentName} mounted`, props);
  }

  componentUnmount(componentName: string): void {
    this.debug('Component', `${componentName} unmounted`);
  }

  componentError(componentName: string, error: Error, context?: any): void {
    this.error('Component', `${componentName} error`, error, context);
  }

  // State management logging
  stateChange(store: string, action: string, data?: any): void {
    this.debug('State', `${store} - ${action}`, data);
  }

  // User action logging
  userAction(action: string, data?: any): void {
    this.info('User', action, data);
  }

  // Authentication logging
  authSuccess(userId: string, method: string): void {
    this.info('Auth', `User ${userId} authenticated via ${method}`);
  }

  authFailure(method: string, error: Error): void {
    this.error('Auth', `Authentication failed via ${method}`, error);
  }


  // Get logs from file storage (last 7 days)
  async getStoredLogs(days: number = 7): Promise<LogEntry[]> {
    if (typeof window === 'undefined') return [];
    
    const allLogs: LogEntry[] = [];
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    try {
      // Get logs from file system
      if ((window as any).logDirectoryHandle) {
        const fileSystemLogs = await this.getFileSystemLogs(cutoffDate);
        allLogs.push(...fileSystemLogs);
      }
    } catch (error) {
      // Silently fail if retrieval fails
    }
    
    // Sort by timestamp
    return allLogs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  private async getFileSystemLogs(cutoffDate: Date): Promise<LogEntry[]> {
    const logs: LogEntry[] = [];
    
    try {
      const dirHandle = (window as any).logDirectoryHandle;
      const logsDir = await dirHandle.getDirectoryHandle('logs');
      
      // Get all log files
      for await (const [name, handle] of logsDir.entries()) {
        if (handle.kind === 'file' && name.startsWith('frontend-logs-') && name.endsWith('.log')) {
          const dateStr = name.replace('frontend-logs-', '').replace('.log', '');
          const logDate = new Date(dateStr);
          
          if (logDate >= cutoffDate) {
            try {
              const fileHandle = await logsDir.getFileHandle(name);
              const file = await fileHandle.getFile();
              const content = await file.text();
              if (content) {
                const parsedLogs = this.parseLogFile(content);
                logs.push(...parsedLogs);
              }
            } catch (error) {
              // Silently fail if file reading fails
            }
          }
        }
      }
    } catch (error) {
      // Silently fail if file system access fails
    }
    
    return logs;
  }

  private parseLogFile(content: string): LogEntry[] {
    const logs: LogEntry[] = [];
    const lines = content.split('\n').filter(line => line.trim());
    
    for (const line of lines) {
      try {
        // Parse log line format: "2025-01-01 12:00:00 [INFO ] [category] message"
        const match = line.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\s*\] \[([^\]]+)\] (.+)$/);
        if (match) {
          const [, timestamp, level, category, message] = match;
          const logLevel = this.parseLogLevel(level.trim());
          
          logs.push({
            timestamp: new Date(timestamp).toISOString(),
            level: logLevel,
            category,
            message
          });
        }
      } catch (error) {
        // Skip malformed log lines
      }
    }
    
    return logs;
  }

  private parseLogLevel(level: string): LogLevel {
    switch (level.toUpperCase()) {
      case 'DEBUG': return LogLevel.DEBUG;
      case 'INFO': return LogLevel.INFO;
      case 'WARN': return LogLevel.WARN;
      case 'ERROR': return LogLevel.ERROR;
      default: return LogLevel.INFO;
    }
  }


  // Clear all stored logs
  async clearStoredLogs(): Promise<void> {
    if (typeof window === 'undefined') return;
    
    try {
      // Clear file system logs if available
      if ((window as any).logDirectoryHandle) {
        await this.clearFileSystemLogs();
      }
    } catch (error) {
      // Silently fail if cleanup fails
    }
  }

  private async clearFileSystemLogs(): Promise<void> {
    try {
      const dirHandle = (window as any).logDirectoryHandle;
      const logsDir = await dirHandle.getDirectoryHandle('logs');
      
      // Get all log files and remove them
      const filesToRemove: string[] = [];
      for await (const [name, handle] of logsDir.entries()) {
        if (handle.kind === 'file' && name.startsWith('frontend-logs-') && name.endsWith('.log')) {
          filesToRemove.push(name);
        }
      }
      
      // Remove all log files
      for (const fileName of filesToRemove) {
        try {
          await logsDir.removeEntry(fileName);
        } catch (error) {
          // Silently fail if file removal fails
        }
      }
    } catch (error) {
      // Silently fail if file system access fails
    }
  }


  // Export stored logs for debugging
  async exportStoredLogs(days: number = 7): Promise<string> {
    const storedLogs = await this.getStoredLogs(days);
    return JSON.stringify(storedLogs, null, 2);
  }

  // Get log statistics
  async getLogStats(): Promise<{ total: number; byLevel: Record<string, number>; byCategory: Record<string, number> }> {
    const storedLogs = await this.getStoredLogs(7);
    const stats = {
      total: storedLogs.length,
      byLevel: {} as Record<string, number>,
      byCategory: {} as Record<string, number>
    };
    
    storedLogs.forEach(log => {
      const levelName = LogLevel[log.level];
      const category = log.category;
      
      stats.byLevel[levelName] = (stats.byLevel[levelName] || 0) + 1;
      stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    });
    
    return stats;
  }
}

// Create singleton instance
export const logger = new Logger();

// Export convenience functions
export const logDebug = (category: string, message: string, data?: any) => logger.debug(category, message, data);
export const logInfo = (category: string, message: string, data?: any) => logger.info(category, message, data);
export const logWarn = (category: string, message: string, data?: any, error?: Error) => logger.warn(category, message, data, error);
export const logError = (category: string, message: string, error?: Error, data?: any) => logger.error(category, message, error, data);

// Export API logging functions
export const logApiRequest = (endpoint: string, method: string, data?: any) => logger.apiRequest(endpoint, method, data);
export const logApiResponse = (endpoint: string, method: string, status: number, data?: any) => logger.apiResponse(endpoint, method, status, data);
export const logApiError = (endpoint: string, method: string, error: Error, data?: any) => logger.apiError(endpoint, method, error, data);

// Export component logging functions
export const logComponentMount = (componentName: string, props?: any) => logger.componentMount(componentName, props);
export const logComponentUnmount = (componentName: string) => logger.componentUnmount(componentName);
export const logComponentError = (componentName: string, error: Error, context?: any) => logger.componentError(componentName, error, context);

// Export state logging functions
export const logStateChange = (store: string, action: string, data?: any) => logger.stateChange(store, action, data);

// Export user action logging functions
export const logUserAction = (action: string, data?: any) => logger.userAction(action, data);

// Export auth logging functions
export const logAuthSuccess = (userId: string, method: string) => logger.authSuccess(userId, method);
export const logAuthFailure = (method: string, error: Error) => logger.authFailure(method, error);

// Export log management functions
export const getStoredLogs = (days: number = 7) => logger.getStoredLogs(days);
export const clearStoredLogs = () => logger.clearStoredLogs();
export const exportStoredLogs = (days: number = 7) => logger.exportStoredLogs(days);
export const getLogStats = () => logger.getLogStats();

// Make logger accessible from browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
  (window as any).getStoredLogs = getStoredLogs;
  (window as any).exportStoredLogs = exportStoredLogs;
  (window as any).getLogStats = getLogStats;
  (window as any).clearStoredLogs = clearStoredLogs;
  
  // Add a helper function to request log directory
  (window as any).requestLogDirectory = async () => {
    try {
      const dirHandle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });
      (window as any).logDirectoryHandle = dirHandle;
      console.log('Log directory set successfully! Logs will now be stored in files.');
      return dirHandle;
    } catch (error) {
      console.log('Failed to set log directory:', error);
    }
  };
  
  // Log initial message
  console.log('Frontend Logger initialized. Use requestLogDirectory() to set up file logging.');
  console.log('📁 Logs will be stored locally in the selected directory.');
  
  // Test logging
  logger.info('Frontend', 'Frontend logger initialized and ready - logs stored locally');
  
  // Add test function to browser console
  (window as any).testFrontendLogging = () => {
    console.log('🧪 Testing frontend logging (local files only)...');
    logger.info('Test', 'This is a test log message - stored locally');
    logger.error('Test', 'This is a test error message - stored locally');
    logger.warn('Test', 'This is a test warning message - stored locally');
  };
}

export default logger;
